import {
  LayoutDashboard,
  FolderKanban,
  Layers,
  Users,
  UserCheck,
  Globe,
  Archive,
  BadgeDollarSign,
  Kanban,
  Clock,
  FileText,
  Briefcase,
  BookOpen,
  DollarSign,
  CreditCard,
  Receipt,
  Settings,
  CalendarCheck,
  Building2,
  BarChart2,
  TrendingUp,
  ClipboardList,
} from 'lucide-react'

export type SectionKey = 'company' | 'sales' | 'accounts' | 'projects' | 'people' | 'hr'

export interface SectionDefinition {
  key: SectionKey
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  color: string
  bgColor: string
  // The sidebar section keys that should be visible when this section is selected
  sidebarSections: string[]
  // The defaultRoute for this section
  defaultRoute: string
}

// Define the sidebar section keys that match AppLayout.tsx
export const SIDEBAR_SECTIONS = {
  workspace: 'Workspace',
  leads: 'Leads',
  deals: 'Deals',
  salesDocs: 'Sales Docs',
  sales: 'Sales',
  people: 'People',
  system: 'System',
} as const

export const SECTION_DEFINITIONS: Record<SectionKey, SectionDefinition> = {
  company: {
    key: 'company',
    label: 'Company',
    description: 'Complete workspace overview with all modules',
    icon: Building2,
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-950/50',
    sidebarSections: ['workspace', 'leads', 'deals', 'salesDocs', 'sales', 'people', 'system'],
    defaultRoute: '/app',
  },
  sales: {
    key: 'sales',
    label: 'Sales',
    description: 'Leads, Deals, Quotations & Sales Operations',
    icon: TrendingUp,
    color: 'text-rose-600 dark:text-rose-400',
    bgColor: 'bg-rose-50 dark:bg-rose-950/50',
    sidebarSections: ['leads', 'deals', 'salesDocs', 'sales'],
    defaultRoute: '/app/leads',
  },
  accounts: {
    key: 'accounts',
    label: 'Accounts',
    description: 'Quotations, Invoices, Payments & Contacts',
    icon: DollarSign,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-950/50',
    sidebarSections: ['sales'],
    defaultRoute: '/app/sales/quotations',
  },
  projects: {
    key: 'projects',
    label: 'Projects',
    description: 'Spaces, Portfolio Timeline & Project Management',
    icon: FolderKanban,
    color: 'text-violet-600 dark:text-violet-400',
    bgColor: 'bg-violet-50 dark:bg-violet-950/50',
    sidebarSections: ['projects'],
    defaultRoute: '/spaces',
  },
  // Alias of `hr` for code that still uses the 'people' key. Not offered in the
  // workspace picker (see DashboardPage availableSections).
  people: {
    key: 'people',
    label: 'People',
    description: 'HRMS, Team Directory, Onboarding, Leaves & Performance',
    icon: Users,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/50',
    sidebarSections: ['people'],
    defaultRoute: '/app/hr/dashboard',
  },
  hr: {
    key: 'hr',
    label: 'HR',
    description: 'People, Attendance & Settings',
    icon: Users,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-950/50',
    sidebarSections: ['people', 'hr'],
    defaultRoute: '/app/hr/dashboard',
  },
}

// Section-specific dashboard widget configurations
export const SECTION_DASHBOARD_TABS: Record<SectionKey, { id: string; label: string; widgets: string[] }[]> = {
  company: [
    {
      id: 'overview',
      label: 'Overview',
      widgets: ['companyHeadline', 'companyActions', 'companySales', 'companyCollections', 'companyDelivery', 'companyPeople'],
    },
    {
      id: 'workspace',
      label: 'Workspace',
      widgets: ['myDay', 'taskKpis', 'tasksBySpace', 'projects', 'activeSprints', 'teamWorkload'],
    },
  ],
  sales: [
    {
      id: 'pipeline',
      label: 'Pipeline',
      widgets: ['salesKpis', 'leads', 'deals', 'quotationPipeline', 'meetingsReminders'],
    },
    {
      id: 'leads',
      label: 'Leads',
      widgets: ['salesKpis', 'leads', 'meetingsReminders'],
    },
    {
      id: 'deals',
      label: 'Deals',
      widgets: ['salesKpis', 'deals'],
    },
  ],
  accounts: [
    {
      id: 'overview',
      label: 'Overview',
      widgets: ['accountsKpis', 'milestonesDue', 'approvalQueue', 'recentRequests'],
    },
    {
      id: 'receivables',
      label: 'Receivables',
      widgets: ['accountsKpis', 'milestonesDue'],
    },
    {
      id: 'approvals',
      label: 'Approvals',
      widgets: ['approvalQueue', 'recentRequests'],
    },
  ],
  projects: [
    {
      id: 'overview',
      label: 'Overview',
      widgets: ['taskKpis', 'tasksBySpace', 'projects', 'activeSprints', 'teamWorkload'],
    },
    {
      id: 'portfolio',
      label: 'Portfolio',
      widgets: ['projects', 'activeSprints'],
    },
  ],
  people: [
    {
      id: 'overview',
      label: 'Overview',
      widgets: ['hrKpis', 'headcount', 'attendanceToday', 'pendingTimesheets'],
    },
    {
      id: 'people',
      label: 'People',
      widgets: ['hrKpis', 'headcount', 'companyPeople'],
    },
    {
      id: 'attendance',
      label: 'Attendance',
      widgets: ['attendanceToday', 'pendingTimesheets'],
    },
  ],
  hr: [
    {
      id: 'overview',
      label: 'Overview',
      widgets: ['hrKpis', 'headcount', 'attendanceToday', 'pendingTimesheets'],
    },
    {
      id: 'people',
      label: 'People',
      widgets: ['hrKpis', 'headcount', 'companyPeople'],
    },
    {
      id: 'attendance',
      label: 'Attendance',
      widgets: ['attendanceToday', 'pendingTimesheets'],
    },
  ],
}

// Helper to get visible sidebar sections for a given section
export function getVisibleSidebarSections(sectionKey: SectionKey): string[] {
  return SECTION_DEFINITIONS[sectionKey]?.sidebarSections || []
}

// Helper to check if a sidebar section should be visible
export function isSidebarSectionVisible(sectionKey: string, selectedSection: SectionKey | null): boolean {
  if (!selectedSection || selectedSection === 'company') return true // Show all when no section selected or company
  const visibleSections = getVisibleSidebarSections(selectedSection)
  return visibleSections.includes(sectionKey)
}